import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import proj4 from 'proj4';

// MUI
import { Box } from '@mui/material';

// OpenLayers
import { register as OlRegister } from 'ol/proj/proj4';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
import OlBaseLayer from 'ol/layer/Base';
import OlLayerGroup from 'ol/layer/Group';
import OlCollection from 'ol/Collection';
import OlSourceVector from 'ol/source/Vector';
import OlFeature from 'ol/Feature';
import OlFormatGeoJSON from 'ol/format/GeoJSON';
import OlFormatWKT from 'ol/format/WKT';
import OlPoint from 'ol/geom/Point';
import { Circle, Fill, RegularShape, Stroke, Style } from 'ol/style';
import { FeatureLike } from 'ol/Feature';
import { buffer as olBuffer } from 'ol/extent';

// Custom components
import { Controls, RotateControl, ScaleLineControl, ZoomControl, ZoomToExtentControl } from '@/components/Map/Controls';
import { Layers, VectorLayer } from '@/components/Map/Layers';
import GeoBaseLayerSwitcher from '@/components/Map/Controls/GEO/GeoBaseLayerSwitcher';
import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import Map from '@/components/Map/Map';

// Context
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';

// Lib
import { flattenLayers, padExtent } from '@/lib/olHelpers';

// Services
import mapService from '@/services/mapService';
import gsService from '@/services/gsService';
import { SimulacijaRezultat } from '@/services/plinService';

export type ZatvaranjeVentilaMapMode = 'none' | 'ventili' | 'curenje';

interface IZatvaranjeVentilaMapProps {
  mapId: number;
  mode: ZatvaranjeVentilaMapMode;
  rezultat: SimulacijaRezultat | null;
  leakWkt: string | null;
  onValveClick: (ventilIds: number[]) => void;
  onLeakClick: (wkt: string) => void;
  focusPotrosac: { id: number; seq: number } | null; // seq so clicking the same row zooms again
}

const geojson = new OlFormatGeoJSON();
const wktFormat = new OlFormatWKT();

// cyan + black/white only, base layers already use yellow, blue, red, purple and orange
const CYAN = '#00e5ff';
const pipeStyle = [
  new Style({ stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.75)', width: 9 }) }),
  new Style({ stroke: new Stroke({ color: CYAN, width: 5 }) }),
];
const consumerStyle = new Style({
  image: new Circle({ radius: 5, fill: new Fill({ color: CYAN }), stroke: new Stroke({ color: '#000', width: 1.5 }) }),
});
const mrsStyle = new Style({
  image: new RegularShape({
    points: 4,
    radius: 12,
    angle: Math.PI / 4,
    fill: new Fill({ color: '#000' }),
    stroke: new Stroke({ color: CYAN, width: 3 }),
  }),
});
const leakStyle = [
  new Style({
    image: new Circle({
      radius: 15,
      fill: new Fill({ color: 'rgba(255, 255, 255, 0.85)' }),
      stroke: new Stroke({ color: '#000', width: 3 }),
    }),
  }),
  new Style({ image: new Circle({ radius: 6, fill: new Fill({ color: CYAN }), stroke: new Stroke({ color: '#000', width: 1.5 }) }) }),
];
const valveShape = (fill: string, stroke: string, radius = 10, strokeWidth = 2) =>
  new Style({
    image: new RegularShape({
      points: 3,
      radius,
      fill: new Fill({ color: fill }),
      stroke: new Stroke({ color: stroke, width: strokeWidth }),
    }),
  });
const valveStyles = {
  closed: valveShape('#000', '#fff', 11, 2.5),
  closedOffGraph: valveShape('rgba(0, 0, 0, 0.35)', '#fff', 9),
  needed: valveShape('#000', '#fff', 11, 2.5),
  optional: valveShape('#fff', '#6b7280', 8),
};
const focusStyle = new Style({
  image: new Circle({ radius: 14, stroke: new Stroke({ color: '#000', width: 3 }), fill: new Fill({ color: 'rgba(0, 229, 255, 0.35)' }) }),
});
const valveStyle = (f: FeatureLike) => {
  const needed = f.get('needed');
  if (needed === true) return valveStyles.needed;
  if (needed === false) return valveStyles.optional;
  return f.get('in_graph') ? valveStyles.closed : valveStyles.closedOffGraph;
};

// stable reference, Map refits the view whenever this prop changes identity
const ZOOM_PADDING = [60, 60, 60, 60];

const readFeatures = (fc: object | undefined) => (fc ? (geojson.readFeatures(fc) as OlFeature[]) : []);

const ZatvaranjeVentilaMap = (props: IZatvaranjeVentilaMapProps) => {
  const { mapId, mode, rezultat, leakWkt, onValveClick, onLeakClick, focusPotrosac } = props;
  const { t } = useTranslation();
  const tenantContext = useContext(TenantContext) as TenantContextType;

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [zoomExtent, setZoomExtent] = useState<number[] | undefined>(undefined);

  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  OlRegister(proj4);

  const defaultExtent = useMemo(
    () =>
      padExtent(
        tenantContext?.mapSettings?.default_extent ? tenantContext.mapSettings.default_extent : [1437016, 5271097, 2206278, 5860579]
      ),
    []
  );

  const viewOptions = useMemo(
    () => ({
      center: tenantContext?.mapSettings?.initial_view_center
        ? tenantContext.mapSettings.initial_view_center
        : ([1731757, 5581737] as [number, number]),
      extent: tenantContext?.mapSettings?.max_extent ? tenantContext.mapSettings.max_extent : [1437016, 5271097, 2206278, 5860579],
      zoom: tenantContext?.mapSettings?.initial_view_zoom ? tenantContext.mapSettings.initial_view_zoom : 8,
      minZoom: tenantContext?.mapSettings?.map_min_view_zoom ? tenantContext.mapSettings.map_min_view_zoom : 8,
      maxZoom: tenantContext?.mapSettings?.map_max_view_zoom ? tenantContext.mapSettings.map_max_view_zoom : 21,
    }),
    [tenantContext]
  );

  useEffect(() => {
    mapService.getLayers(mapId).then((coll) => setLayersCollection(coll));
  }, [mapId]);

  const pipesSource = useMemo(() => new OlSourceVector({ features: readFeatures(rezultat?.plinovodi) }), [rezultat]);
  const consumersSource = useMemo(() => new OlSourceVector({ features: readFeatures(rezultat?.potrosaci) }), [rezultat]);
  const valvesSource = useMemo(() => new OlSourceVector({ features: readFeatures(rezultat?.ventili) }), [rezultat]);
  const mrsSource = useMemo(() => new OlSourceVector({ features: readFeatures(rezultat?.mrs) }), [rezultat]);
  const leakSource = useMemo(
    () => new OlSourceVector({ features: leakWkt ? [new OlFeature(wktFormat.readGeometry(leakWkt))] : [] }),
    [leakWkt]
  );

  const focusFeature = useMemo(
    () => (focusPotrosac ? consumersSource.getFeatures().find((f) => f.get('id') === focusPotrosac.id) : undefined),
    [focusPotrosac, consumersSource]
  );
  const focusSource = useMemo(() => new OlSourceVector({ features: focusFeature ? [focusFeature.clone()] : [] }), [focusFeature]);

  useEffect(() => {
    const geom = focusFeature?.getGeometry();
    if (geom) setZoomExtent(olBuffer(geom.getExtent(), 40));
  }, [focusPotrosac?.seq]);

  // zoom once per scenario, never on valve toggles or leak moves
  const zoomedForId = useRef<number | null>(null);
  useEffect(() => {
    if (!rezultat) {
      zoomedForId.current = null;
      return;
    }
    if (zoomedForId.current === rezultat.id) return;
    zoomedForId.current = rezultat.id;
    const extentSource = pipesSource.getFeatures().length ? pipesSource : leakSource.getFeatures().length ? leakSource : valvesSource;
    if (extentSource.getFeatures().length) {
      // a lone valve or leak point has a zero-size extent, which would zoom to max
      setZoomExtent(olBuffer(extentSource.getExtent(), 150));
    }
  }, [rezultat]);

  const handleClick = (evt: OlMapBrowserEvent<any>) => {
    if (mode === 'curenje') {
      onLeakClick(wktFormat.writeGeometry(new OlPoint(evt.coordinate)));
      return;
    }
    if (mode !== 'ventili' || !layersCollection) return;

    const queryLayers = flattenLayers(layersCollection.getArray())
      .filter((x) => !(x instanceof OlLayerGroup))
      .filter((x) => x.get('query') === true);
    if (!queryLayers.length) return;

    gsService.getFeatureInfo(evt.map, evt.pixel, queryLayers).then((resp) => {
      if (!resp) return;
      // all valve ids under the click, FME duplicates share a location
      const ids = Object.values(resp)
        .flat()
        .map((f: any) => Number(f?.properties?.id))
        .filter((id) => Number.isFinite(id));
      if (ids.length) {
        onValveClick(Array.from(new Set(ids)));
      }
    });
  };

  if (!layersCollection) return null;

  return (
    <Box sx={{ height: '100%', '& .ol-viewport': { cursor: mode === 'none' ? 'default' : 'crosshair' } }}>
      <Map
        height="100%"
        view={viewOptions}
        onClick={handleClick}
        id="zatvaranje-ventila-map"
        zoomToExtent={zoomExtent}
        zoomToExtentPadding={ZOOM_PADDING}
        initialized
      >
        <Controls>
          <ZoomControl zoomInTipLabel={`${t('map:controls.zoom_in')}`} zoomOutTipLabel={`${t('map:controls.zoom_out')}`} />
          <RotateControl autoHide={false} />
          <ScaleLineControl type="minimap" />
          <GeoBaseLayerSwitcher type="minimap" mapId={mapId} allowNoLayer={false} />
          <ZoomToExtentControl
            id="zoom-extent-default"
            extent={defaultExtent}
            tipLabel={`${t('map:controls.zoom_to_extent')}`}
            className="ol-control ol-zoom-extent"
          />
        </Controls>
        <Layers>
          <GeoAPILayers layersCollection={layersCollection} />
          <VectorLayer id="plin-rez-plinovodi" source={pipesSource} style={pipeStyle} zIndex={900} />
          <VectorLayer id="plin-rez-potrosaci" source={consumersSource} style={consumerStyle} zIndex={910} />
          <VectorLayer id="plin-rez-mrs" source={mrsSource} style={mrsStyle} zIndex={920} />
          <VectorLayer id="plin-rez-ventili" source={valvesSource} style={valveStyle} zIndex={930} />
          <VectorLayer id="plin-rez-curenje" source={leakSource} style={leakStyle} zIndex={940} />
          <VectorLayer id="plin-rez-fokus" source={focusSource} style={focusStyle} zIndex={950} />
        </Layers>
      </Map>
    </Box>
  );
};

export default ZatvaranjeVentilaMap;
